import React, { memo, useRef } from 'react';
import { pdfStyles } from '../../pdfStyles';
import { Page, View } from '@react-pdf/renderer';
import {
  ControlsTable,
  Footer,
  FooterStartComponent,
  Header,
  HeaderEndComponent,
  NoData,
  RmpPage,
  TitleLink,
} from '../../comps';
import { isEqual } from 'lodash';
import { ActionsConf, AssessmentPDF, ControlPDF, ScenarioPDF, TocItem } from '../../types';
import { ColorSet } from 'src/redux/reducers/theme';
import { getCurrencySymbol } from 'src/utils/misc';

interface RmpRenderProps {
  assessment: AssessmentPDF;
  onDone: (pageNumber: number, toc: TocItem[]) => void;
  controls: ControlPDF[];
  colors: ColorSet;
  scenarios: ScenarioPDF[];
  includesActions: ActionsConf;
  logoSrc?: string;
  secondaryColor?: string | null;
}

export const RmpRender: React.FC<RmpRenderProps> = memo(
  ({ assessment, onDone, controls, colors, scenarios, includesActions, logoSrc, secondaryColor }) => {
    const mainPageNumber = useRef(0);
    const symbol = getCurrencySymbol();

    const list: { [key: string]: TocItem } = {};

    const onRender = (pageNumber: number, idx: number, title: string) => {
      list[idx] = { pageNumber: pageNumber, title };

      if (idx === controls.length - 1) {
        const outputArray = Object.keys(list).map((key) => list[key]);
        onDone(mainPageNumber.current, outputArray);
      }
    };

    const transformedControls = controls.map((control) => {
      const modifiersText: string[] = [];

      // Get modifiers from risk management plans for this control
      const modifiers = control?.riskManagementPlans?.flatMap((rmp) => rmp.modifiers || []) || [];

      modifiers.forEach((modifier) => {
        switch (modifier?.type) {
          case 'TREAT_REDUCE_CONSEQUENCE':
            modifiersText.push(
              `Reduces consequence by ${modifier.isPercentage ? `${modifier.consequenceLower}%` : `${symbol}${modifier.consequenceLower}`} (Lower) and ${
                modifier.isPercentage ? `${modifier.consequenceUpper}%` : `${symbol}${modifier.consequenceUpper}`
              } (Upper)`,
            );
            break;
          case 'TREAT_REDUCE_FREQUENCY':
            modifiersText.push(
              `Reduces frequency by ${modifier.isPercentage ? `${modifier.frequencyReduction}%` : `#${modifier.frequencyReduction}`}`,
            );
            break;
          case 'NOTE_TYPE':
            modifiersText.push(`Note: ${modifier.note}`);
            break;
          case 'TRANSFER_LIMIT':
            modifiersText.push(
              `Reduces risk with a total policy cover of ${symbol}${modifier.totalPolicyCover?.toLocaleString()} and an excess of ${symbol}${modifier.excess?.toLocaleString()}`,
            );
            break;
          default:
            modifiersText.push('Unknown modifier type');
            break;
        }
      });

      return {
        ...control,
        modifiersText,
      };
    });

    return (
      <>
        <Page size={'A4'} wrap style={pdfStyles.page}>
          <Header endComponent={<HeaderEndComponent title="BACK TO TOP" />} colors={colors} logoSrc={logoSrc} />
          <View
            // @ts-ignore
            render={(data) => {
              if (!controls.length) {
                onDone(data.pageNumber, []);
              } else {
                mainPageNumber.current = data.pageNumber;
              }
            }}
          />
          <View style={pdfStyles.section}>
            <TitleLink
              title={'RISK MANAGEMENT PLAN'}
              intLinkId={'rmp.plan'}
              extLink={`risk-management`}
              colors={colors}
            />

            <View style={pdfStyles.spacer8} />
            <View style={pdfStyles.spacer4} />
            {transformedControls.length ? (
              <ControlsTable data={controls.filter((c): c is NonNullable<typeof c> => Boolean(c))} colors={colors} />
            ) : (
              <NoData colors={colors} />
            )}
          </View>

          <Footer
            footerComponent={<FooterStartComponent title={`${assessment.name} / Risk Management Plan`} />}
            colors={colors}
          />
        </Page>
        {transformedControls.map((el, idx) => {
          return (
            <RmpPage
              key={el.id}
              control={el}
              scenarios={scenarios}
              onRender={(pageNumber) => onRender(pageNumber, idx, el.name || '')}
              endComponent={<HeaderEndComponent title="BACK TO TOP" />}
              footerComponent={
                <FooterStartComponent title={`${assessment.name} / Risk Management Plan / ${el.name}`} />
              }
              colors={colors}
              includesActions={includesActions}
              logoSrc={logoSrc}
              secondaryColor={secondaryColor}
            />
          );
        })}
      </>
    );
  },
  (prevProps, nextProps) => isEqual(prevProps, nextProps),
);
